import { useRef } from 'react';
import { Button, Checkbox, Input, Radio, Slider, Typography, message } from 'antd';
import type { ContractPrintSettings } from '../../contract/types';

interface Props {
  settings: ContractPrintSettings;
  onChange: (s: ContractPrintSettings) => void;
}

export function ContractSettings({ settings, onChange }: Props) {
  const patch = (p: Partial<ContractPrintSettings>) => onChange({ ...settings, ...p });
  const wmFileRef = useRef<HTMLInputElement | null>(null);

  const onWmImage = (file: File | undefined) => {
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp)$/.test(file.type)) {
      message.error('请上传 PNG/JPG 图片');
      return;
    }
    if (file.size > 1024 * 1024) {
      message.error('水印图过大（>1MB）');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => patch({ watermarkImage: String(reader.result) });
    reader.readAsDataURL(file);
  };

  return (
    <div>
      <div style={{ marginBottom: 8 }}>
        <Typography.Text type="secondary" style={{ fontSize: 11 }}>纸张</Typography.Text>
        <Radio.Group
          size="small"
          value={settings.paperSize}
          onChange={(e) => patch({ paperSize: e.target.value })}
          optionType="button"
          options={['A4', 'A5', 'A3', 'Letter']}
          style={{ display: 'block', marginTop: 2 }}
        />
      </div>
      <div style={{ marginBottom: 8 }}>
        <Typography.Text type="secondary" style={{ fontSize: 11 }}>方向</Typography.Text>
        <Radio.Group
          size="small"
          value={settings.orientation}
          onChange={(e) => patch({ orientation: e.target.value })}
          optionType="button"
          options={[
            { label: '纵向', value: 'portrait' },
            { label: '横向', value: 'landscape' },
          ]}
          style={{ display: 'block', marginTop: 2 }}
        />
      </div>
      <div style={{ marginBottom: 8 }}>
        <Typography.Text type="secondary" style={{ fontSize: 11 }}>
          边距 {settings.marginMm}mm
        </Typography.Text>
        <Slider
          min={0}
          max={40}
          value={settings.marginMm}
          onChange={(v) => patch({ marginMm: v })}
        />
      </div>
      <div>
        <Typography.Text type="secondary" style={{ fontSize: 11 }}>
          基础字号 {settings.baseFontSizePx}px
        </Typography.Text>
        <Slider
          min={8}
          max={24}
          value={settings.baseFontSizePx}
          onChange={(v) => patch({ baseFontSizePx: v })}
        />
      </div>
      <div style={{ marginTop: 8 }}>
        <Typography.Text type="secondary" style={{ fontSize: 11 }}>水印文字（防伪/草稿，留空=无）</Typography.Text>
        <Input
          size="small"
          placeholder="如：仅供内部 / 作废"
          value={settings.watermarkText ?? ''}
          onChange={(e) => patch({ watermarkText: e.target.value })}
          style={{ marginTop: 2 }}
        />
      </div>
      <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <Typography.Text type="secondary" style={{ fontSize: 11 }}>图片水印</Typography.Text>
        {settings.watermarkImage ? (
          <>
            <img src={settings.watermarkImage} alt="水印" style={{ height: 24, opacity: 0.6, border: '1px solid #eee' }} />
            <Button size="small" onClick={() => wmFileRef.current?.click()}>更换</Button>
            <Button size="small" type="text" danger onClick={() => patch({ watermarkImage: undefined })}>移除</Button>
          </>
        ) : (
          <Button size="small" onClick={() => wmFileRef.current?.click()}>上传 logo（淡化居中）</Button>
        )}
        <input
          ref={wmFileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          style={{ display: 'none' }}
          onChange={(e) => {
            onWmImage(e.target.files?.[0]);
            e.target.value = '';
          }}
        />
      </div>
      <div style={{ marginTop: 8 }}>
        <Typography.Text type="secondary" style={{ fontSize: 11 }}>页眉（每页顶部，仅打印时逐页显示）</Typography.Text>
        <Input
          size="small"
          placeholder="如：XX公司 · 保密"
          value={settings.headerText ?? ''}
          onChange={(e) => patch({ headerText: e.target.value })}
          style={{ marginTop: 2 }}
        />
      </div>
      <div style={{ marginTop: 8 }}>
        <Typography.Text type="secondary" style={{ fontSize: 11 }}>页脚（每页底部，仅打印时逐页显示）</Typography.Text>
        <Input
          size="small"
          placeholder="如：本单据一式两份"
          value={settings.footerText ?? ''}
          onChange={(e) => patch({ footerText: e.target.value })}
          style={{ marginTop: 2 }}
        />
      </div>
      <div style={{ marginTop: 10 }}>
        <Checkbox
          checked={!!settings.continuousPage}
          onChange={(e) => patch({ continuousPage: e.target.checked })}
        >
          <Typography.Text style={{ fontSize: 12 }}>连续页面（导出 PDF 不分页，长单据成一页）</Typography.Text>
        </Checkbox>
      </div>
      <div style={{ marginTop: 8 }}>
        <Typography.Text type="secondary" style={{ fontSize: 11 }}>导出文件名（{'{字段名}'} 变量，留空=默认）</Typography.Text>
        <Input
          size="small"
          placeholder="如：报价单_{客户名称}"
          value={settings.exportFileName ?? ''}
          onChange={(e) => patch({ exportFileName: e.target.value })}
          style={{ marginTop: 2 }}
        />
      </div>
    </div>
  );
}
